import { useMemo, useState } from 'react';
import type {
  WaScope,
  WhatsappAccount,
  WhatsappAnalytics,
  WhatsappCampaign,
} from '../../api/types';
import { useWaAnalytics } from '../../api/whatsapp';
import { formatNumber, formatPercent } from '../../format';
import { Badge, SelectField, Stat } from '../ui';
import { ErrorState, SkeletonStats } from '../states';
import { DataTable } from '../table/DataTable';
import { QualityBadge } from './shared';

const DAY_MS = 86_400_000;

/**
 * Delivery, reads, replies and failures for a campaign or a period,
 * each figure against the previous period of equal length (the hook takes
 * from/to, so the comparison is a second request, never a guess). Counts
 * take no decimals, rates one. A campaign has no previous
 * period, so its figures carry the period label instead.
 */
export function AnalyticsPanel({
  account,
  campaigns,
  scope = {},
}: {
  account: WhatsappAccount | null;
  campaigns: WhatsappCampaign[] | null;
  scope?: WaScope;
}) {
  const [campaignId, setCampaignId] = useState('');
  const [days, setDays] = useState('30');
  const n = Number(days);
  /** Fixed per period so the query keys (and so the requests) only change with the filters. */
  const range = useMemo(() => {
    const now = Date.now();
    return {
      from: new Date(now - n * DAY_MS).toISOString(),
      previousFrom: new Date(now - 2 * n * DAY_MS).toISOString(),
      previousTo: new Date(now - n * DAY_MS).toISOString(),
    };
  }, [n]);
  const campaign = campaignId ? { campaignId } : {};
  const analytics = useWaAnalytics({ ...campaign, from: range.from }, scope);
  const previous = useWaAnalytics({ from: range.previousFrom, to: range.previousTo }, scope, {
    enabled: !campaignId,
  });
  const data = analytics.data;
  const prev = campaignId ? null : (previous.data ?? null);
  const periodLabel = `vs previous ${n} days`;

  const t = data?.totals;
  return (
    <div className="stack">
      <div className="filters">
        <SelectField
          label="Campaign"
          value={campaignId}
          onChange={(e) => setCampaignId(e.target.value)}
        >
          <option value="">All messages</option>
          {(campaigns ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>
        <SelectField label="Period" value={days} onChange={(e) => setDays(e.target.value)}>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </SelectField>
        {account ? (
          <div className="field">
            <span className="field__label">Number</span>
            <span className="badges">
              <QualityBadge rating={account.qualityRating} />
              {account.messagingLimit ? (
                <Badge tone="neutral">{account.messagingLimit.replace('TIER_', 'Limit ')}</Badge>
              ) : null}
            </span>
          </div>
        ) : null}
      </div>
      {analytics.isError ? (
        <ErrorState
          error={analytics.error}
          onRetry={() => analytics.refetch()}
          retrying={analytics.isFetching}
        />
      ) : null}
      {analytics.isPending ? <SkeletonStats count={5} /> : null}
      {data && t ? (
        <>
          <div className="stats">
            <Stat
              label="Sent"
              value={formatNumber(t.sent)}
              reference={
                campaignId
                  ? `in the last ${n} days${t.queued ? ` · ${formatNumber(t.queued)} queued` : ''}`
                  : reference(prev?.totals.sent, periodLabel)
              }
              trend={campaignId ? undefined : trend(t.sent, prev?.totals.sent, 0)}
            />
            <Stat
              label="Delivered"
              value={formatPercent(data.rates.deliveredPct)}
              reference={`${formatNumber(t.delivered)} of ${formatNumber(t.sent)} sent`}
              trend={
                campaignId
                  ? undefined
                  : trend(data.rates.deliveredPct, prev?.rates.deliveredPct, 1, ' pts')
              }
            />
            <Stat
              label="Read"
              value={formatPercent(data.rates.readPct)}
              reference={`${formatNumber(t.read)} of ${formatNumber(t.delivered)} delivered`}
              trend={
                campaignId ? undefined : trend(data.rates.readPct, prev?.rates.readPct, 1, ' pts')
              }
            />
            <Stat
              label="Replied"
              value={formatPercent(data.rates.replyPct)}
              reference={`${formatNumber(t.replied)} replies · ${formatNumber(t.received)} inbound`}
              trend={
                campaignId ? undefined : trend(data.rates.replyPct, prev?.rates.replyPct, 1, ' pts')
              }
            />
            <Stat
              label="Failed"
              value={formatNumber(t.failed)}
              reference={
                t.skipped
                  ? `${formatNumber(t.skipped)} skipped by consent`
                  : campaignId
                    ? `in the last ${n} days`
                    : reference(prev?.totals.failed, periodLabel)
              }
              trend={campaignId ? undefined : trend(t.failed, prev?.totals.failed, 0)}
            />
          </div>
          {!campaignId && previous.isError ? (
            <p className="muted">
              Could not load the previous period; figures show without a comparison.
            </p>
          ) : null}
          {data.daily.length ? (
            <>
              <DailyChart daily={data.daily} />
              <DataTable
                label="Daily totals"
                columns={[
                  { key: 'day', header: 'Day' },
                  { key: 'sent', header: 'Sent', align: 'num' },
                  { key: 'delivered', header: 'Delivered', align: 'num' },
                  { key: 'read', header: 'Read', align: 'num' },
                  { key: 'replied', header: 'Replied', align: 'num' },
                  { key: 'failed', header: 'Failed', align: 'num' },
                  { key: 'skipped', header: 'Skipped', align: 'num' },
                  { key: 'inbound', header: 'Inbound', align: 'num' },
                ]}
              >
                {data.daily.map((d) => (
                  <tr key={d.day}>
                    <td>
                      <time dateTime={d.day}>{d.day}</time>
                    </td>
                    <td className="num">{formatNumber(d.sent)}</td>
                    <td className="num">{formatNumber(d.delivered)}</td>
                    <td className="num">{formatNumber(d.read)}</td>
                    <td className="num">{formatNumber(d.replied)}</td>
                    <td className="num">{formatNumber(d.failed)}</td>
                    <td className="num">{formatNumber(d.skipped)}</td>
                    <td className="num">{formatNumber(d.received)}</td>
                  </tr>
                ))}
              </DataTable>
            </>
          ) : (
            <p className="muted">No messages in this period.</p>
          )}
        </>
      ) : null}
    </div>
  );
}

/** The previous period's figure next to its label, or just the label while it is unknown. */
function reference(before: number | undefined, label: string): string {
  if (before === undefined) return label;
  return `${label}: ${formatNumber(before)}`;
}

/**
 * The signed change against the previous period, as an arrow direction and
 * a label ("+12", "−0.4 pts", "+$3.10"). No previous figure, no trend.
 */
export function trend(
  current: number | null,
  before: number | null | undefined,
  digits: number,
  unit = '',
  prefix = '',
): { direction: 'up' | 'down' | 'flat'; label: string } | undefined {
  if (current === null || before === null || before === undefined) return undefined;
  const delta = current - before;
  const rounded = Number(delta.toFixed(digits));
  const direction = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat';
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  return {
    direction,
    label: `${sign}${prefix}${formatNumber(Math.abs(rounded), digits)}${unit}`,
  };
}

/**
 * Messages sent per day as one ink line with labelled axes. The daily table
 * below carries every value; this only shows the shape. No library, no
 * gradient; each point has a title with its value.
 */
function DailyChart({ daily }: { daily: WhatsappAnalytics['daily'] }) {
  if (daily.length < 2) return null;
  const width = 640;
  const height = 160;
  const pad = { top: 12, right: 12, bottom: 28, left: 40 };
  const max = Math.max(1, ...daily.map((d) => d.sent));
  const x = (i: number) => pad.left + (i / (daily.length - 1)) * (width - pad.left - pad.right);
  const y = (v: number) => pad.top + (1 - v / max) * (height - pad.top - pad.bottom);
  const path = daily
    .map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(d.sent).toFixed(1)}`)
    .join(' ');
  const first = daily[0]!;
  const last = daily[daily.length - 1]!;
  return (
    <figure className="chart">
      <figcaption className="chart__caption">
        Messages sent per day, {first.day} to {last.day}
      </figcaption>
      <svg
        className="chart__svg"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Messages sent per day from ${first.day} to ${last.day}, peaking at ${formatNumber(max)}`}
      >
        <line
          className="chart__axis"
          x1={pad.left}
          y1={height - pad.bottom}
          x2={width - pad.right}
          y2={height - pad.bottom}
        />
        <line
          className="chart__axis"
          x1={pad.left}
          y1={pad.top}
          x2={pad.left}
          y2={height - pad.bottom}
        />
        <text className="chart__tick" x={pad.left - 6} y={pad.top + 4} textAnchor="end">
          {formatNumber(max)}
        </text>
        <text className="chart__tick" x={pad.left - 6} y={height - pad.bottom} textAnchor="end">
          0
        </text>
        <text className="chart__tick" x={pad.left} y={height - 8} textAnchor="start">
          {first.day}
        </text>
        <text className="chart__tick" x={width - pad.right} y={height - 8} textAnchor="end">
          {last.day}
        </text>
        <path className="chart__line" d={path} />
        {daily.map((d, i) => (
          <circle key={d.day} className="chart__point" cx={x(i)} cy={y(d.sent)} r={3}>
            <title>{`${d.day}: ${formatNumber(d.sent)} sent`}</title>
          </circle>
        ))}
      </svg>
    </figure>
  );
}
